import { orderBy } from "lodash-es";
import { IntlShape } from "react-intl";

import SavedAddressType, {
  ReservedSavedAddressType,
} from "@mapmycustomers/shared/enum/SavedAddressType";
import SavedAddress from "@mapmycustomers/shared/types/SavedAddress";
import getFormattedAddressForUi from "@mapmycustomers/shared/util/formatters/getFormattedAddressForUi";

import locatedToAddress from "@app/util/locatedToAddress";

import getSavedAddressDisplayName from "./getSavedAddressDisplayName";

const RESERVED_SAVED_ADDRESS_TYPES: ReservedSavedAddressType[] = [
  SavedAddressType.HOME,
  SavedAddressType.WORK,
];
const TYPE_RANK: Record<SavedAddressType, number> = {
  [SavedAddressType.CUSTOM]: 2,
  [SavedAddressType.HOME]: 0,
  [SavedAddressType.WORK]: 1,
};

export const getSavedAddressLine = (savedAddress: SavedAddress) =>
  getFormattedAddressForUi(locatedToAddress(savedAddress));

export const sortSavedAddresses = (savedAddresses: SavedAddress[]) =>
  orderBy(
    savedAddresses,
    [({ type }) => TYPE_RANK[type], ({ lastUsedAt }) => lastUsedAt ?? "", "createdAt", "id"],
    ["asc", "desc", "desc", "desc"]
  );

export const getSavedAddressList = (
  intl: IntlShape,
  savedAddresses: SavedAddress[],
  query: string
): { savedAddresses: SavedAddress[]; unsetTypes: ReservedSavedAddressType[] } => {
  const text = query.trim().toLowerCase();
  if (!text) {
    return {
      savedAddresses,
      unsetTypes: RESERVED_SAVED_ADDRESS_TYPES.filter(
        (type) => !savedAddresses.some((savedAddress) => savedAddress.type === type)
      ),
    };
  }
  return {
    savedAddresses: savedAddresses.filter((savedAddress) =>
      [getSavedAddressDisplayName(intl, savedAddress), getSavedAddressLine(savedAddress)].some(
        (value) => value.toLowerCase().includes(text)
      )
    ),
    unsetTypes: [],
  };
};

// A custom name is the rep's own words, so text found there and not in the address stays private;
// one or two letters reveal nothing, and keep the pickers' early suggestions
export const isSavedAddressNameQuery = (savedAddresses: SavedAddress[], query: string) => {
  const text = query.trim().toLowerCase();
  return (
    text.length >= 3 &&
    savedAddresses.some(
      (savedAddress) =>
        savedAddress.type === SavedAddressType.CUSTOM &&
        !!savedAddress.name?.toLowerCase().includes(text) &&
        !getSavedAddressLine(savedAddress).toLowerCase().includes(text)
    )
  );
};

// The group's order: Home or its Set row, Work or its Set row, then the custom rows
export const mapSavedAddressList = <T>(
  { savedAddresses, unsetTypes }: ReturnType<typeof getSavedAddressList>,
  mapSavedAddress: (savedAddress: SavedAddress) => T,
  mapUnsetType: (type: ReservedSavedAddressType) => T
): T[] => [
  ...RESERVED_SAVED_ADDRESS_TYPES.flatMap((type) => {
    const savedAddress = savedAddresses.find((row) => row.type === type);
    if (savedAddress) {
      return [mapSavedAddress(savedAddress)];
    }
    return unsetTypes.includes(type) ? [mapUnsetType(type)] : [];
  }),
  ...savedAddresses
    .filter(({ type }) => type === SavedAddressType.CUSTOM)
    .map((savedAddress) => mapSavedAddress(savedAddress)),
];

export const findSavedAddress = (
  savedAddresses: SavedAddress[],
  { placeId }: Partial<Pick<SavedAddress, "placeId">>
) =>
  placeId ? savedAddresses.find((savedAddress) => savedAddress.placeId === placeId) : undefined;
